import React, { useCallback, useMemo, useState } from 'react'
import { useDetectionExplorerStore, useToggleSelectedDetection } from '../DetectionExplorerStore'
import { resolveKnownCountry } from '../config'
import { formatDetectionTimestamp } from '../formatTimestamp'
import { DetectionResult } from '../get_detections'
import { GalleryUI } from './GalleryUI'
import { GalleryItem } from './GalleryCard'
import { DetectionDetailModal } from './DetectionDetailModal'

export const toGalleryItem = (d: DetectionResult): GalleryItem => ({
    id: d.detection_uuid,
    licensePlate: d.license_plate ?? '',
    country: resolveKnownCountry(d.country),
    direction: d.direction,
    vehicleType: d.vehicle_type,
    timestamp: formatDetectionTimestamp(d.timestamp),
    deviceId: d.device_id,
    imageUrl: d.images.render.url ?? d.images.roi.url ?? undefined,
    lpImageUrl: d.images.lp.url ?? undefined,
    category: d.category
})

type Props = {
    items: DetectionResult[]
}

export const GalleryContainer: React.FC<Props> = ({ items }) => {
    const selectedById = useDetectionExplorerStore((s) => s.selectedById)
    const handleToggle = useToggleSelectedDetection(items)

    const [activeDetectionId, setActiveDetectionId] = useState<string | null>(null)

    // Image / LP-case filtering now happens upstream (see useFilteredDetections); the gallery just
    // renders whatever items it's given.
    const galleryItems = useMemo(() => items.map(toGalleryItem), [items])
    const activeDetection = useMemo(
        () => items.find((d) => d.detection_uuid === activeDetectionId) ?? null,
        [items, activeDetectionId]
    )

    const handleOpenDetail = useCallback((id: string) => setActiveDetectionId(id), [])
    const handleCloseDetail = useCallback(() => setActiveDetectionId(null), [])

    return (
        <>
            <GalleryUI
                items={galleryItems}
                selectedIds={Object.keys(selectedById)}
                onToggleSelect={handleToggle}
                onOpenDetail={handleOpenDetail}
            />
            {activeDetection && <DetectionDetailModal detection={activeDetection} onClose={handleCloseDetail} />}
        </>
    )
}
